"use client";

// The one part of the topbar title a route cannot know statically: the editor's
// "Jay Crotty · Week 5". Every other title is a constant in lib/page-headers.ts
// and needs none of this.
//
// It earns a context because the topbar lives in the layout, ABOVE the page that
// knows the answer — the editor resolves its creator and week from the ?id and
// the acting persona, and nothing higher up can. Without this the week number
// appears nowhere: WeekStatusCard says "This week", not which one.
//
// And the crumbs a route cannot know statically: an affiliate brand's pod
// ("Affiliate Campaigns › Acsah's pod › KiZE concept"), which is a fact about the
// brand, not the path. The topbar draws them after the path's constant crumbs
// and before the title.

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { Crumb } from "@/lib/page-headers";

interface PageMeta {
  meta: string | null;
  setMeta: (value: string | null) => void;
  /** Crumbs the page knows and the path does not, drawn after the path's own. */
  crumbs: Crumb[];
  setCrumbs: (value: Crumb[]) => void;
  /** "Whatever the path says this page is, it is not that." See NoPageTitle. */
  titleHidden: boolean;
  setTitleHidden: (value: boolean) => void;
}

const Ctx = createContext<PageMeta>({
  meta: null,
  setMeta: () => {},
  crumbs: [],
  setCrumbs: () => {},
  titleHidden: false,
  setTitleHidden: () => {},
});

export function PageMetaProvider({ children }: { children: React.ReactNode }) {
  const [meta, setMeta] = useState<string | null>(null);
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [titleHidden, setTitleHidden] = useState(false);
  const value = useMemo(
    () => ({ meta, setMeta, crumbs, setCrumbs, titleHidden, setTitleHidden }),
    [meta, crumbs, titleHidden],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const usePageMeta = () => useContext(Ctx);

const NO_CRUMBS: Crumb[] = [];

/**
 * Renders nothing; publishes `value` (and any `crumbs`) to the topbar for as long
 * as it is mounted. Clearing on unmount is what stops a stale "Week 5", or a
 * stale pod, from trailing you back out to the list.
 *
 * ⚠️ THE CRUMBS ARE COMPARED BY WHAT THEY SAY, NOT BY IDENTITY. A server page
 * hands a fresh array on every render (a refresh, a tab change), and keying the
 * effect on the array would clear and re-set the topbar each time for nothing.
 */
export function PageMeta({ value, crumbs = NO_CRUMBS }: { value: string; crumbs?: Crumb[] }) {
  const { setMeta, setCrumbs } = usePageMeta();
  const said = JSON.stringify(crumbs);
  useEffect(() => {
    setMeta(value);
    setCrumbs(JSON.parse(said) as Crumb[]);
    return () => {
      setMeta(null);
      setCrumbs(NO_CRUMBS);
    };
  }, [value, said, setMeta, setCrumbs]);
  return null;
}

/**
 * TAKE THE PATH'S NAME OFF THE TOPBAR. Renders nothing; used by the 404.
 *
 * ⚠️ IT EXISTS BECAUSE THE 404 IS A PERMISSION ANSWER NOW. A page you may not
 * open answers notFound() (lib/server/page-gate.tsx), and the topbar resolves
 * its title from the PATHNAME — so a creator refused /scripts/rubric got "This
 * page doesn't exist." under a heading reading "How Scripts Get Graded". The
 * screen contradicted itself, and worse, it leaked the title of a page the
 * refusal exists to say nothing about. Caught by looking at a screenshot.
 *
 * A CLIENT LEAF IMPORTED BY A SERVER PAGE, which is the compromise that keeps
 * app/not-found.tsx's own rule intact: that file has no "use client" and its way
 * out is a plain <a>, so the screen still works when the JavaScript is the thing
 * that has gone wrong. With JS dead the anchor still navigates and the only loss
 * is a stale heading — the right way round for this to degrade.
 */
export function NoPageTitle() {
  const { setTitleHidden } = usePageMeta();
  useEffect(() => {
    setTitleHidden(true);
    return () => setTitleHidden(false);
  }, [setTitleHidden]);
  return null;
}
